import { DisclosureProps, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { toDocumentWithEmbeddedEntries, isRichTextDocument } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import { ModuleList, ModuleTypes, ModuleTypeName, isMappedModule } from './modules';

// Module-level store for current page's disclosure numbering
// This is safe in SSR since each request gets its own module instance
let currentPageDisclosureNumberingMap: DisclosureNumberingMap | undefined;

export type DisclosureNumberingMap = Map<string, number>;

// Disclosure with module index for ordering
export type IndexedDisclosureProps = DisclosureProps & { moduleIndex: number };

/**
 * Shared utility function to sort disclosures by symbols first, then module order, then original order
 * This ensures consistent sorting across both server-side and client-side disclosure processing
 */
export function sortDisclosuresByPriority(disclosures: DisclosureProps[]): DisclosureProps[] {
  return [...disclosures]
    .map((disclosure, index) => ({
      disclosure,
      originalIndex: index,
      moduleIndex: (disclosure as IndexedDisclosureProps).moduleIndex ?? Infinity,
    }))
    .sort((a, b) => {
      const aHasSymbol = !!a.disclosure.symbol;
      const bHasSymbol = !!b.disclosure.symbol;

      // If both have symbols or both don't have symbols, sort by module order then original order
      if (aHasSymbol === bHasSymbol) {
        // First sort by module index (page order) - lower index = earlier on page = lower number
        if (a.moduleIndex !== b.moduleIndex) {
          return a.moduleIndex - b.moduleIndex;
        }
        // Then by original index within the module
        return a.originalIndex - b.originalIndex;
      }

      // Put disclosures with symbols first
      return aHasSymbol ? -1 : 1;
    })
    .map(item => item.disclosure);
}

/**
 * Set the current page's disclosure numbering map
 */
export function setCurrentPageDisclosureNumberingMap(map: DisclosureNumberingMap) {
  currentPageDisclosureNumberingMap = map;
}

/**
 * Get the current page's disclosure numbering map
 */
export function getCurrentPageDisclosureNumberingMap(): DisclosureNumberingMap | undefined {
  return currentPageDisclosureNumberingMap;
}

/**
 * Type guard for the legacy `{ json, links }` shape (vs. a raw rich-text Document).
 */
// Type predicate: the input must be as wide as what callers pass.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
const isLegacyRichTextDocument = (value: unknown): value is DocumentWithEmbeddedEntries => {
  // Narrows an unparsed value before reading it; the parse happens here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  return value !== null && typeof value === 'object' && !Array.isArray(value) && 'json' in value;
};

/**
 * Extract all embedded Disclosure entries from any document with inline entries
 */
// Decodes an arbitrary value at its boundary and returns a narrowed result.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
export function extractDisclosuresFromDocument(document: unknown): DisclosureProps[] {
  // Entry-native fields arrive as raw rich-text Documents - convert to the legacy
  // `{ json, links }` shape so the inline-entry extraction below is shape-agnostic.
  if (isRichTextDocument(document)) {
    return extractDisclosuresFromDocument(toDocumentWithEmbeddedEntries(document));
  }

  if (!isLegacyRichTextDocument(document) || !document.links?.entries?.inline) {
    return [];
  }

  return document.links.entries.inline.flatMap(entry => {
    switch (entry.__typename) {
      case 'Disclosure':
        return entry.copy ? [{ sys: entry.sys, symbol: entry.symbol, copy: entry.copy }] : [];
      case 'InlineStyledText':
        return extractDisclosuresFromDocument(entry.content);
      case 'Modal':
        return extractDisclosuresFromDocument(entry.copy);
      default:
        return [];
    }
  });
}

/**
 * Generic path navigator function
 */
// Generic path navigator over arbitrary objects, so neither the input nor the value at the path is constrained.
// oxlint-disable-next-line anti-slop/no-unknown-returns, anti-slop/no-unknown-parameters
function navigateToPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce(
    (current, part) =>
      // Narrows an unparsed value before reading it; the parse happens here.
      // oxlint-disable-next-line anti-slop/no-runtime-typeof
      current && typeof current === 'object' && current !== null && part in current
        ? // Holds an unparsed value; the reader narrows it at the point of use.
          // oxlint-disable-next-line anti-slop/no-unsafe-dictionary-type
          (current as Record<string, unknown>)[part]
        : undefined,
    obj,
  );
}

/**
 * Extract disclosures from any path/field combination
 */
function extractFromPath(
  // Walks any module by a path expression, so the value at that path is unconstrained.
  // oxlint-disable-next-line anti-slop/no-unknown-parameters
  module: unknown,
  path: string,
  fields: string[],
  isArray = false,
  nestedPath?: string,
  nestedExtraction = false,
): DisclosureProps[] {
  const target = path ? navigateToPath(module, path) : module;

  if (!target) return [];

  // Handle nested extraction for PageSection modules
  if (nestedExtraction && isArray && Array.isArray(target)) {
    return extractFromNestedModules(target);
  }

  // Handle nested arrays (for complex cases like FAQ)
  if (nestedPath && Array.isArray(target)) {
    return extractFromNestedArrays(target, nestedPath, fields);
  }

  // Handle arrays
  if (isArray && Array.isArray(target)) {
    return extractFromItems(target, fields);
  }

  // Handle direct fields
  if (!isArray && isObjectWithKeys(target)) {
    return extractFromFields(target, fields);
  }

  return [];
}

/**
 * Extract disclosures from nested array structures
 */
function extractFromNestedArrays(target: unknown[], nestedPath: string, fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  target.forEach(item => {
    const nestedItems = navigateToPath(item, nestedPath);
    if (Array.isArray(nestedItems)) {
      disclosures.push(...extractFromItems(nestedItems, fields));
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from nested modules (like PageSection or SectionHero)
 */
function extractFromNestedModules(nestedModules: unknown[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  nestedModules.forEach(nestedModule => {
    // Narrows an unparsed value before reading it; the parse happens here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (nestedModule && typeof nestedModule === 'object' && '__typename' in nestedModule) {
      const nestedDisclosures = extractDisclosuresFromModule(nestedModule);
      disclosures.push(...nestedDisclosures);
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from an array of items
 */
function extractFromItems(items: unknown[], fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  items.forEach(item => {
    if (isObjectWithKeys(item)) {
      disclosures.push(...extractFromFields(item, fields));
    }
  });

  return disclosures;
}

/**
 * Extract disclosures from a single object's fields
 */
// Holds an unparsed value; the reader narrows it at the point of use.
// oxlint-disable-next-line anti-slop/no-unsafe-dictionary-type
function extractFromFields(target: Record<string, unknown>, fields: string[]): DisclosureProps[] {
  const disclosures: DisclosureProps[] = [];

  // Entry-native modules carry their rich-text fields under `fields` (e.g.
  // `module.fields.headline`), legacy adapter-flattened modules carry them
  // top-level. Read top-level first, then fall back to the nested `fields`.
  const entryFields = isObjectWithKeys(target.fields) ? target.fields : undefined;

  fields.forEach(field => {
    const value = (field in target ? target[field] : undefined) ?? entryFields?.[field];
    if (value) {
      const fieldDisclosures = extractDisclosuresFromDocument(value);
      disclosures.push(...fieldDisclosures);
    }
  });

  return disclosures;
}

/**
 * Type guard to check if target is an object with string keys
 */
// Type predicate: the input must be as wide as what callers pass.
// oxlint-disable-next-line anti-slop/no-unknown-parameters, anti-slop/no-unsafe-dictionary-type
const isObjectWithKeys = (obj: unknown): obj is Record<string, unknown> => {
  // Narrows an unparsed value before reading it; the parse happens here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
};

// Common field presets
const FIELD_PRESETS = {
  HEADLINE_COPY: ['headline', 'copy'],
  HEADLINE_ONLY: ['headline'],
  COPY_ONLY: ['copy'],
  CONTENT_ONLY: ['content'],
  CARD_STANDARD: ['headline', 'headlineSmallRte', 'copy'],
  CARD_HEADLINE_COPY: ['cardHeadlineRte', 'copy'],
  ACCORDION_FIELDS: ['accordionLabelRte', 'accordionCopy'],
};

// Simplified configuration using presets and extraction rules
const MODULE_EXTRACTION_RULES: Partial<
  Record<
    ModuleTypeName,
    Array<{
      path?: string;
      fields: string[];
      isArray?: boolean;
      nestedPath?: string;
      nestedExtraction?: boolean;
    }>
  >
  // Keyed by a name computed at runtime, so the index signature is the contract.
  // oxlint-disable-next-line anti-slop/no-known-value-widening
> = {
  Accordion: [
    { fields: FIELD_PRESETS.HEADLINE_COPY },
    { path: 'fields.content', fields: FIELD_PRESETS.ACCORDION_FIELDS, isArray: true },
  ],
  AnimatedMarquee: [{ fields: FIELD_PRESETS.COPY_ONLY }],
  BentoBox: [
    { fields: ['eyebrowRte', 'headline', 'copy'] },
    { path: 'largeCard', fields: ['headlineRte', 'copy'] },
    { path: 'cards.items', fields: FIELD_PRESETS.CARD_HEADLINE_COPY, isArray: true },
    { path: 'fields.largeCard', fields: ['cardHeadlineRte', 'cardCopy'] },
    { path: 'fields.cards', fields: ['cardHeadlineRte', 'cardCopy', 'copy'], isArray: true },
  ],
  CalloutIcon: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'content.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CalloutImage: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'content.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CalloutGrid: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'cards.items', fields: FIELD_PRESETS.CARD_STANDARD, isArray: true },
  ],
  CardsExpandable: [
    { path: 'fields.content', fields: ['eyebrow', 'cardHeadline', 'cardCopy'], isArray: true },
    { path: 'fields.content', nestedPath: 'fields.iconList', fields: ['copy'], isArray: true },
  ],
  CardsGridComplex: [{ path: 'fields.cards', fields: ['eyebrowRte', 'headline', 'copy'], isArray: true }],
  CardsGridSimple: [{ path: 'fields.cards', fields: FIELD_PRESETS.HEADLINE_COPY, isArray: true }],
  CardsIcon: [{ path: 'fields.content', fields: ['textEyebrowRte', 'cardHeadlineRte', 'cardCopy'], isArray: true }],
  CardsImage: [{ path: 'fields.content', fields: ['cardHeadlineRte', 'cardCopy'], isArray: true }],
  CardsStacked: [{ path: 'fields.cards', fields: ['eyebrow', 'headline', 'shortDescription'], isArray: true }],
  CardCarousel: [{ path: 'fields.cards', fields: ['eyebrow', 'headline', 'copy'], isArray: true }],
  CardGrid: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'cardGridItems.items', fields: FIELD_PRESETS.HEADLINE_COPY, isArray: true },
  ],
  CardsVerticalScroll: [{ path: 'cards.items', fields: ['headline', 'copy'], isArray: true }],
  Compare: [{ fields: FIELD_PRESETS.CONTENT_ONLY }],
  ContentBlock: [{ fields: ['eyebrow', 'headline', 'copy'] }],
  CopyAddOnWithButton: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  CrossSell: [{ fields: ['title', 'copy'] }],
  DisclosureBanner: [{ fields: FIELD_PRESETS.COPY_ONLY }],
  Explainer: [
    { fields: FIELD_PRESETS.HEADLINE_COPY },
    { path: 'explainerSlides.items', fields: FIELD_PRESETS.HEADLINE_COPY, isArray: true },
  ],
  Faq: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    {
      path: 'toggleItems.items',
      fields: FIELD_PRESETS.ACCORDION_FIELDS,
      isArray: true,
      nestedPath: 'contentCollection.items',
    },
    {
      path: 'fields.content',
      fields: FIELD_PRESETS.ACCORDION_FIELDS,
      isArray: true,
      nestedPath: 'fields.content',
    },
  ],
  Features: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  FullBleedImageHeadline: [{ fields: ['eyebrow', 'headline'] }],
  HeadlineWithEyebrow: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  HeroBento: [
    { fields: ['eyebrowText', 'headline'] },
    { path: 'cards.items', fields: ['copy', 'disclaimers'], isArray: true },
  ],
  HeroConsolidated: [
    { fields: ['eyebrowText', 'headline', 'copy', 'disclaimers'] },
    { path: 'disclaimerModal', fields: FIELD_PRESETS.COPY_ONLY },
  ],
  HubSpot: [{ fields: ['headline', 'eyebrow'] }],
  ImpactHeadline: [{ fields: ['richTextHeadline', 'copy'] }],
  Launchpad: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  MapLocator: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  MediaCopy: [
    { fields: ['eyebrowText', 'headline', 'copy'] },
    { path: 'sliderEntryCollection.items', fields: ['content'], isArray: true },
  ],
  MediaCopyDivided: [{ fields: ['headline'] }, { path: 'fields.copy', fields: ['copy'], isArray: true }],
  Note: [{ fields: FIELD_PRESETS.HEADLINE_COPY }],
  PageSection: [{ path: 'modulesCollection.items', fields: [], isArray: true, nestedExtraction: true }],
  SectionHero: [
    { path: 'hero', fields: ['eyebrowText', 'headline', 'copy', 'disclaimers'] },
    { path: 'hero.disclaimerModal', fields: FIELD_PRESETS.COPY_ONLY },
    { path: 'heroConsolidated', fields: [], isArray: false, nestedExtraction: true },
  ],
  ResourcesItgBranded: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'resources.items', fields: ['resourceLabelRte', 'resourceCopy'], isArray: true },
  ],
  RichTextEditor: [{ fields: FIELD_PRESETS.CONTENT_ONLY }],
  TeaserLinks: [{ fields: FIELD_PRESETS.HEADLINE_ONLY }],
  TabbedMediaCopy: [{ path: 'tabs.items', fields: FIELD_PRESETS.HEADLINE_COPY, isArray: true }],
  Testimonial: [
    { fields: FIELD_PRESETS.HEADLINE_ONLY },
    { path: 'testimonialItems.items', fields: ['copy', 'attributionRte'], isArray: true },
  ],
};

function hasExtractionRules(value: string): value is ModuleTypeName {
  return Object.hasOwn(MODULE_EXTRACTION_RULES, value);
}

function extractDisclosuresFromModule(module: unknown): DisclosureProps[] {
  if (!isObjectWithKeys(module) || typeof module.__typename !== 'string' || !hasExtractionRules(module.__typename)) {
    return [];
  }

  try {
    const rules = MODULE_EXTRACTION_RULES[module.__typename];
    if (!rules) {
      return [];
    }

    const disclosures = rules.flatMap(
      ({ path = '', fields, isArray = false, nestedPath, nestedExtraction = false }) => {
        const pathDisclosures = extractFromPath(module, path, fields, isArray, nestedPath, nestedExtraction);
        return pathDisclosures;
      },
    );

    return disclosures;
  } catch (error) {
    console.error('Error extracting disclosures from module:', module.__typename, error);
    return [];
  }
}

/**
 * Deduplicate indexed disclosures by sys.id (keeping the earliest occurrence by
 * moduleIndex) and return them sorted by render priority. Shared by the module-
 * and document-based extractors so both order and dedupe disclosures identically.
 */
function dedupeIndexedDisclosures(allDisclosures: IndexedDisclosureProps[]): DisclosureProps[] {
  const disclosureMap = new Map<string, IndexedDisclosureProps>();

  allDisclosures.forEach(disclosure => {
    const id = disclosure.sys?.id;
    if (!id) return;

    const existing = disclosureMap.get(id);
    if (!existing || disclosure.moduleIndex < existing.moduleIndex) {
      disclosureMap.set(id, disclosure);
    }
  });

  return sortDisclosuresByPriority(Array.from(disclosureMap.values()));
}

export function extractPageDisclosures(modules: unknown[]): DisclosureProps[] {
  const allDisclosures: IndexedDisclosureProps[] = [];

  modules.forEach((module, moduleIndex) => {
    const moduleDisclosures = extractDisclosuresFromModule(module);
    // Add module index to preserve page ordering
    const indexedDisclosures: IndexedDisclosureProps[] = moduleDisclosures.map(disclosure => ({
      ...disclosure,
      moduleIndex,
    }));

    allDisclosures.push(...indexedDisclosures);
  });

  return dedupeIndexedDisclosures(allDisclosures);
}

/**
 * Extract embedded disclosures from a list of rich-text documents, preserving
 * their order across documents (each document's index stands in for moduleIndex).
 * Used by templates whose body content lives in rich-text fields rather than
 * modules — e.g. agreement pages (finePrint + section bodies).
 */
export function extractDocumentDisclosures(
  documents: Array<DocumentWithEmbeddedEntries | undefined>,
): DisclosureProps[] {
  const allDisclosures: IndexedDisclosureProps[] = [];

  documents.forEach((document, documentIndex) => {
    extractDisclosuresFromDocument(document).forEach(disclosure => {
      allDisclosures.push({ ...disclosure, moduleIndex: documentIndex });
    });
  });

  return dedupeIndexedDisclosures(allDisclosures);
}

/**
 * Filters header/footer modules out of a content list. Used by templates that
 * extract page disclosures from main-content modules only.
 */
export function getDisclosureSourceModules(modules: ModuleTypes[ModuleTypeName][]): ModuleTypes[ModuleTypeName][] {
  return modules.filter(
    module => !isMappedModule(module, ModuleList.HEADER) && !isMappedModule(module, ModuleList.FOOTER),
  );
}

/**
 * Filters main-content modules, extracts every embedded disclosure, builds the
 * inline numbering map, and installs it as the current-page map. Returns the
 * ordered disclosures so callers can render their footer disclosure list.
 */
export function setupPageDisclosures(modules: ModuleTypes[ModuleTypeName][]): DisclosureProps[] {
  const pageDisclosures = extractPageDisclosures(getDisclosureSourceModules(modules));
  setCurrentPageDisclosureNumberingMap(createDisclosureNumberingMap(pageDisclosures));
  return pageDisclosures;
}

/**
 * Document-field counterpart to `setupPageDisclosures`: extracts embedded
 * disclosures from rich-text documents (in order), builds the inline numbering
 * map, installs it as the current-page map, and returns the ordered disclosures
 * for footer rendering. Used by agreement pages, whose body lives in finePrint +
 * section bodies rather than modulesCollection.
 */
export function setupDocumentDisclosures(documents: Array<DocumentWithEmbeddedEntries | undefined>): DisclosureProps[] {
  const pageDisclosures = extractDocumentDisclosures(documents);
  setCurrentPageDisclosureNumberingMap(createDisclosureNumberingMap(pageDisclosures));
  return pageDisclosures;
}

/**
 * Create a mapping of disclosure sys.id to auto-assigned numbers for disclosures without symbols
 */
export function createDisclosureNumberingMap(disclosures: DisclosureProps[]): DisclosureNumberingMap {
  const map = new Map<string, number>();
  let autoNumberCounter = 0;

  // Sort disclosures using shared sorting utility
  const sortedDisclosures = sortDisclosuresByPriority(disclosures);

  sortedDisclosures.forEach(disclosure => {
    // Only assign numbers to disclosures that don't have symbols
    if (!disclosure.symbol && disclosure.sys?.id) {
      autoNumberCounter++;
      map.set(disclosure.sys.id, autoNumberCounter);
    }
  });

  return map;
}
